/**
 * A small step indicator for the onboarding wizards.
 *
 * `steps` is [{ key, label }]; `current` is the key being shown; `complete` and
 * `skipped` are arrays of keys. Skipped steps are labelled as skipped rather
 * than ticked, because they are not done.
 */
export function Stepper({ steps, current, complete = [], skipped = [], className = '' }) {
  return (
    <nav aria-label="Progress" className={className}>
      <ol className="flex flex-wrap items-center gap-x-1 gap-y-2 text-sm">
        {steps.map((step, i) => {
          const isCurrent = step.key === current;
          // A step is never "done" while it is the one open: resuming into a step
          // that an earlier session marked complete must not show it ticked.
          const isComplete = !isCurrent && complete.includes(step.key);
          const isSkipped = !isComplete && skipped.includes(step.key);
          const state = isCurrent ? 'current' : isComplete ? 'complete' : isSkipped ? 'skipped' : 'upcoming';

          return (
            <li
              key={step.key}
              aria-current={isCurrent ? 'step' : undefined}
              className="flex items-center gap-1"
            >
              <span
                className={`flex size-6 shrink-0 items-center justify-center rounded-full border text-2xs font-semibold ${
                  isCurrent
                    ? 'border-accent bg-accent text-white'
                    : isComplete
                      ? 'border-positive/40 bg-positive-soft text-positive'
                      : isSkipped
                        ? 'border-rule-strong bg-paper-sunken text-ink-faint'
                        : 'border-rule-strong bg-paper-raised text-ink-faint'
                }`}
                aria-hidden="true"
              >
                {isComplete ? '✓' : isSkipped ? '–' : i + 1}
              </span>
              <span
                className={`${isCurrent ? 'font-medium text-ink' : 'text-ink-muted'} ${
                  isCurrent ? '' : 'hidden sm:inline'
                }`}
              >
                {step.label}
                {state === 'complete' && <span className="sr-only"> (done)</span>}
                {state === 'skipped' && <span className="sr-only"> (skipped)</span>}
              </span>
              {i < steps.length - 1 && (
                <span className="mx-1 hidden h-px w-4 bg-rule-strong sm:inline-block" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
